/* 서버의 중복 로그인 메시지 */
export const SERVER_DOUBLE_LOGIN_ERROR = 'sessionIdCheck';

/* 서버의 로그인 토큰 인증 만료 메시지 */
export const SERVER_TOKEN_EXPIRE_ERROR = 'AccessDenied';

/* 서버에서 받는 API MESSAGE 정보 */
export const API_SERVER_MESSAGE_INFO = {
  'error.pclas.pwd.regist.invalid':
    'PC LAS에서 비밀번호설정 등록 후 사용 가능합니다.  PC의 로그인 화면에 [비밀번호 설정/찾기] 메뉴에서 비밀번호를 등록 해 주세요.',
};

/**
 * API 통신에서 Response가 불확실 할 때, Response 내용 확인해서 구하는 함수
 * @param {object} response response 객체
 * @returns {object} response
 * @author Robin
 */
export const getApiResponse = response => {
  // RTK Query에서, response.data.response 로 존재하는 경우
  // Tablet에서, 통신 에러 난 경우 response.response 로 존재하는 경우
  // 해당 값이 존재한다면 해당 response 값 사용
  return response?.data?.response || response?.response || response;
};

/**
 * API의 Response 객체에서 Status를 추출하는 함수
 * @param {object} response response 객체
 * @return {string | number} response 받은 status
 * @author Robin
 */
export const getApiStatus = (response = {}) => {
  response = getApiResponse(response);
  const status = response?.status;
  return status;
};

/**
 * API 통신에서 Response의 headers 값 구하는 함수
 * @param {object} response response 객체
 * @returns {object} headers 값
 * @author Robin
 */
export const getApiHeaders = response => {
  // Response 값 맞는지 확인 및 맞는 값으로 재세팅
  response = getApiResponse(response);
  const headers = response?.headers;

  return headers;
};

/**
 * API 통신에서 에러 발생 시, 에러 메시지 리턴하는 함수
 * @param {object} response response 객체
 * @param {string} defaultErrorMessage 에러 메시지가 없을 경우 표시할 기본 에러 메시지
 * @returns {string} 에러 메시지
 * @author Robin
 */
export const getApiErrorMessage = (response, defaultErrorMessage) => {
  // Response 값 맞는지 확인 및 맞는 값으로 재세팅
  response = getApiResponse(response);

  const data = getApiData(response);

  const serverMessageId = data?.msgId;
  const serverIdMessage = API_SERVER_MESSAGE_INFO[serverMessageId];
  const serverErrorMessage = data?.detail;
  const axiosErrorMessage = data?.error ? data?.error : data?.Exception;
  const extraMessages = data?.extra?.comErrMsgList;
  defaultErrorMessage = defaultErrorMessage
    ? defaultErrorMessage
    : '데이터를 가져오는데 실패하였습니다.';

  const isDevelopment = process.env.NODE_ENV !== 'development';

  // 개발일 경우는 ID Message > Server Error > Axios Error > Custom Error 순으로 노출
  // 개발이 아닐 경우, ID Message > Server Error > Custom Error 노출
  const displayErrorMessage = serverIdMessage
    ? serverIdMessage
    : serverErrorMessage
    ? serverErrorMessage
    : isDevelopment && axiosErrorMessage
    ? axiosErrorMessage
    : defaultErrorMessage;

  // 상세 에러 메세지가 있는 경우
  if (!$utils.isEmptyArray(extraMessages)) {
    return [displayErrorMessage, ...extraMessages];
  }

  return displayErrorMessage;
};

/**
 * API 통신의 status에 따라 성공여부를 태블릿(saga) / 모바일(RTK) / 일반(Axios) 상관 없이 true / false 로 리턴해주는 함수
 * @param {string | number} status API 호출 후 status
 * @returns {boolean} API 통신의 성공 여부
 * @author Robin
 */
export const checkStatusSuccess = status => {
  const statusText = String(status);

  // Saga / Axios 인 경우는 status가 숫자(200 / "200") 형태로 리턴 되므로,
  // 3자리 이면서 첫 글자가 2로 시작되면 성공으로 판단
  if (statusText.length === 3 && statusText[0] === '2') return true;

  // RTK의 경우 status를
  // Query - pending / rejected / fulfilled
  // Mutation - OK
  // 이므로, fulfilled 또는 OK 로 판단
  if (status === 'fulfilled' || status === 'OK') return true;

  return false;
};

/**
 * API 통신의 response 성공여부를 태블릿(saga) / 모바일(RTK) / 일반(Axios) 상관 없이 true / false 로 리턴해주는 함수
 * @param {object} response API 통신의 결과 response
 * @returns {boolean} API 통신의 성공 여부
 * @author Robin
 */
export const checkApiSuccess = (response = {}) => {
  // Response 값 맞는지 확인 및 맞는 값으로 재세팅
  response = getApiResponse(response);

  // RTK의 Mutation의 경우,
  // API 성공했을 때, Data에만 값이 있음
  // API 실패 했을 때, error가 status를 가지고 있음
  const { data, error, status } = response;

  const statusValue = status || data?.status || error?.status || '';
  const isSuccessStatus = checkStatusSuccess(statusValue);
  const isNoError = !statusValue && !error && !(data && data.isAxiosError);
  const isSuccess = isSuccessStatus || isNoError;

  return isSuccess;
};

/**
 * OMN(EAP) 통신 응답에서 성공/실패 여부 판단하는 함수
 * @param {object} response API 통신의 결과 response
 * @returns {boolean} API 통신의 성공 여부
 * @author Robin
 */
export const checkOmnSuccess = (response = {}) => {
  // Response 값 맞는지 확인 및 맞는 값으로 재세팅
  response = getApiResponse(response);

  const data = getApiData(response);
  const { tranCode } = data;

  const successCode = '0';
  const isOmnSuccess = tranCode === successCode;

  return isOmnSuccess;
};

/**
 * 신계약(NBS) 통신 응답에서 성공/실패 여부 판단하는 함수
 * @param {object} response API 통신의 결과 response
 * @returns {boolean} NBS 응답의 성공여부
 * @author Robin
 */
export const checkNbsSuccess = (response = {}) => {
  // Response 값 맞는지 확인 및 맞는 값으로 재세팅
  response = getApiResponse(response);

  const data = getApiData(response);
  const { header, rbmsPrcsRsltCd } = data;

  let isNbsSuccess = false;
  if (header?.comAnsErr) {
    const successCode = '0';
    isNbsSuccess = String(header?.comAnsErr) === successCode;
  } else if (rbmsPrcsRsltCd) {
    isNbsSuccess = data.rbmsPrcsRsltCd === 'Y' || data.rbmsPrcsRsltCd === 'W';
  }

  return isNbsSuccess;
};

/**
 * 신계약(NBS) 통신 응답에서 메시지를 추출하는 함수
 * @param {object} response response 객체
 * @returns {string[]} 에러 메시지 배열
 * @author Robin
 */
export const getNbsMessage = response => {
  // Response 값 맞는지 확인 및 맞는 값으로 재세팅
  response = getApiResponse(response);

  const data = getApiData(response);
  const { commonMessages, ruleRsltList } = data;

  let messages = [];
  if (commonMessages) {
    messages = commonMessages?.map(messageInfo => messageInfo.comErrMsg) || [];
  } else if (ruleRsltList) {
    messages =
      data.ruleRsltList
        // Object 일 때, stepPlan === "W" 이거나, message가 없는 것은 제외
        ?.filter(rule =>
          typeof rule === 'object'
            ? !$utils.isNil(rule?.message) && rule?.stepPlan !== 'W'
            : rule,
        )
        .map(rule => (typeof rule === 'string' ? rule : rule?.message)) || [];
  }

  const message = $utils.concatMessages(messages);
  return message;
};

/**
 * API 통신의 response의 data를 태블릿(saga) / 모바일(RTK) / 일반(Axios) 통합으로 가져오는 함수
 * @param {object} response API 통신의 결과 response
 * @returns {object} API response data object
 * @author Robin
 */
export const getApiData = (response = {}) => {
  // Response 값 맞는지 확인 및 맞는 값으로 재세팅
  response = getApiResponse(response);

  // Axios / Saga - response.data
  // RTK의 Query - response.data.data / Mutation - data.results
  const data =
    response?.data?.data ||
    response?.data?.RSLT_DATA ||
    response.data?.results ||
    response.data ||
    {};

  return data;
};

/**
 * API 통신의 response의 API 인증 여부 (로그인 토큰 만료 / 중복 로그인) 확인
 * @param {object} response API 통신의 결과 response
 * @returns {boolean} API 인증 유효 여부
 * @author Robin
 */
export const checkApiNotAuthorized = (response = {}) => {
  // Response 값 맞는지 확인 및 맞는 값으로 재세팅
  response = getApiResponse(response);

  const status = response?.status;
  const errorMessage = getApiErrorMessage(response);

  // 중복 로그인 오류 여부 확인
  const isStatus400 = status === 400 || status === '400';
  const isDoubleLoginMessage = errorMessage === SERVER_DOUBLE_LOGIN_ERROR;
  const isDoubleLogin = isStatus400 && isDoubleLoginMessage;

  // 로그인 토큰 만료 여부 확인
  const isStatus401 = status === 401 || status === '401';
  const isTokenExpireMessage = errorMessage === SERVER_TOKEN_EXPIRE_ERROR;
  const isTokenExpire = isStatus401 && isTokenExpireMessage;

  // 인증 되지 않은 API 통신 여부 확인
  const isNotAuthorized = isDoubleLogin || isTokenExpire;

  return isNotAuthorized;
};
